import type { EaseName } from './ease.js';

/**
 * Emitter presets (TRD-04 §3.1).
 *
 * Data, not code, so the dev tuning panel can rewrite them live. Tuning particle feel by
 * editing source and reloading is how a game ends up shipping mediocre feel — you have to
 * be able to change a number while a wave is running and watch it land (R-04-07).
 */

export type ParticleTexture =
  'p_dot1' | 'p_dot2' | 'p_square3' | 'p_spark' | 'p_ring8' | 'p_shard4';

export interface EmitterPreset {
  count: [number, number];
  speed: [number, number];
  /** Radians. Use [0, TAU] for a radial burst; a narrow band aims the cone. */
  angle: [number, number];
  /** Milliseconds. */
  life: [number, number];
  size: [number, number];
  sizeEnd: number;
  drag: number;
  gravity: number;
  /** `'source'` inherits the colour of whatever triggered it — usually a class colour. */
  tint: number[] | 'source';
  tintEnd: number;
  alphaStart: number;
  alphaEnd: number;
  blend: 'normal' | 'add';
  texture: ParticleTexture;
  ease: EaseName;
  /** Radians of spread applied around a supplied direction, for cone emitters. */
  cone?: number;
}

const TAU = Math.PI * 2;

/**
 * Additive blending is doing most of the work in the energetic presets. It is the single
 * highest-impact visual decision in this file: the same particles under normal blending
 * read as confetti, and under additive they read as light.
 */
export const PRESETS = {
  muzzle: {
    count: [6, 9],
    speed: [1.2, 2.6],
    angle: [0, TAU],
    cone: 0.5,
    life: [60, 110],
    size: [1.5, 2.6],
    sizeEnd: 0.2,
    drag: 0.9,
    gravity: 0,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'easeOutQuad',
  },

  /** The lazy grey puff that hangs a beat after the flash has gone. */
  muzzleSmoke: {
    count: [3, 6],
    speed: [0.4, 1.1],
    angle: [0, TAU],
    cone: 0.7,
    life: [180, 340],
    size: [1, 1.9],
    sizeEnd: 2.6,
    drag: 0.9,
    gravity: -0.01,
    tint: [0x5a5560, 0x3d3944],
    tintEnd: 0x22202a,
    alphaStart: 0.4,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'linear',
  },

  impact: {
    count: [11, 16],
    speed: [0.6, 1.8],
    angle: [0, TAU],
    life: [140, 240],
    size: [1.5, 2.8],
    sizeEnd: 0,
    drag: 0.88,
    gravity: 0.01,
    tint: 'source',
    tintEnd: 0x666677,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_dot2',
    ease: 'easeOutQuad',
  },

  impactCrit: {
    count: [24, 34],
    speed: [1.6, 4],
    angle: [0, TAU],
    life: [200, 340],
    size: [2, 3.6],
    sizeEnd: 0,
    drag: 0.9,
    gravity: 0.01,
    tint: [0xffffff, 0xffd60a],
    tintEnd: 0xff6a3d,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  shockwave: {
    count: [1, 1],
    speed: [0, 0],
    angle: [0, 0],
    life: [220, 220],
    size: [0.8, 0.8],
    sizeEnd: 4,
    drag: 1,
    gravity: 0,
    tint: [0xffffff],
    tintEnd: 0xffffff,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_ring8',
    ease: 'easeOutExpo',
  },

  bloodPuff: {
    count: [4, 7],
    speed: [0.3, 0.9],
    angle: [0, TAU],
    life: [180, 300],
    size: [1.4, 2.2],
    sizeEnd: 0.4,
    drag: 0.9,
    gravity: 0.05,
    tint: 'source',
    tintEnd: 0x33222a,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_dot1',
    ease: 'linear',
  },

  death: {
    count: [28, 40],
    speed: [0.8, 2.8],
    angle: [0, TAU],
    life: [280, 460],
    size: [1.7, 3],
    sizeEnd: 0,
    drag: 0.9,
    gravity: 0.03,
    tint: 'source',
    tintEnd: 0x222228,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'easeOutQuad',
  },

  deathBoss: {
    count: [90, 120],
    speed: [1.5, 5.5],
    angle: [0, TAU],
    life: [420, 700],
    size: [2.2, 4.4],
    sizeEnd: 0,
    drag: 0.92,
    gravity: 0.02,
    tint: [0xffffff, 0xffd60a, 0xff6a3d],
    tintEnd: 0x442222,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  poisonTick: {
    count: [1, 1],
    speed: [0.15, 0.35],
    angle: [-Math.PI / 2 - 0.4, -Math.PI / 2 + 0.4],
    life: [500, 700],
    size: [1.4, 1.4],
    sizeEnd: 0.3,
    drag: 0.98,
    gravity: -0.004,
    tint: [0x8ac926],
    tintEnd: 0x4a6a16,
    alphaStart: 0.55,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_dot1',
    ease: 'linear',
  },

  freeze: {
    count: [40, 60],
    speed: [1, 3],
    angle: [0, TAU],
    life: [400, 700],
    size: [1.4, 3.2],
    sizeEnd: 0,
    drag: 0.94,
    gravity: 0,
    tint: [0x4cc9f0, 0xa8e6f7, 0xffffff],
    tintEnd: 0x4cc9f0,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutQuad',
  },

  chainArc: {
    count: [10, 15],
    speed: [0.4, 1.4],
    angle: [0, TAU],
    life: [90, 170],
    size: [1.5, 2.4],
    sizeEnd: 0,
    drag: 0.85,
    gravity: 0,
    tint: [0xdbe9ff, 0xffffff],
    tintEnd: 0x4cc9f0,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'easeOutQuad',
  },

  synergyProc: {
    count: [18, 26],
    speed: [1, 3],
    angle: [0, TAU],
    life: [300, 500],
    size: [1.8, 3.4],
    sizeEnd: 0,
    drag: 0.9,
    gravity: 0,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  goldPop: {
    count: [5, 7],
    speed: [0.6, 1.2],
    angle: [-Math.PI / 2 - 0.6, -Math.PI / 2 + 0.6],
    life: [400, 600],
    size: [1.4, 2],
    sizeEnd: 0.6,
    drag: 0.97,
    gravity: 0.06,
    tint: [0xffd60a, 0xfff3a0],
    tintEnd: 0xffd60a,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot2',
    ease: 'linear',
  },

  coreHit: {
    count: [26, 36],
    speed: [1, 3.4],
    angle: [0, TAU],
    life: [300, 520],
    size: [2, 3.6],
    sizeEnd: 0,
    drag: 0.9,
    gravity: 0.02,
    tint: [0xff006e, 0xff7ab0],
    tintEnd: 0x551122,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_square3',
    ease: 'easeOutQuad',
  },

  placeTower: {
    count: [16, 22],
    speed: [0.8, 2],
    angle: [0, TAU],
    life: [250, 400],
    size: [1.5, 2.8],
    sizeEnd: 0,
    drag: 0.88,
    gravity: 0,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot2',
    ease: 'easeOutQuad',
  },

  trail: {
    count: [1, 1],
    speed: [0, 0.2],
    angle: [0, TAU],
    life: [100, 140],
    size: [1.2, 1.9],
    sizeEnd: 0,
    drag: 0.9,
    gravity: 0,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 0.7,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'linear',
  },

  // ── Explosions ────────────────────────────────────────────────────────────────────
  // Four emitters, not one, because an explosion is four things arriving on different
  // clocks: a flash that is gone in three frames, fire that expands and dies, smoke that
  // outlives both, and embers that fall out of it. Firing them together at one radius is
  // what separates a detonation from the puff a rifle round makes.

  explosionFlash: {
    count: [1, 1],
    speed: [0, 0],
    angle: [0, 0],
    life: [90, 90],
    size: [1, 1],
    sizeEnd: 7,
    drag: 1,
    gravity: 0,
    tint: [0xffffff],
    tintEnd: 0xffd60a,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_ring8',
    ease: 'easeOutExpo',
  },

  explosionFire: {
    count: [32, 46],
    speed: [1.8, 5],
    angle: [0, TAU],
    life: [220, 420],
    size: [2.2, 4.2],
    sizeEnd: 0,
    drag: 0.86,
    gravity: -0.01,
    tint: [0xffffff, 0xffd60a, 0xff8f1f, 0xff6a3d],
    tintEnd: 0xc03018,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot2',
    ease: 'easeOutQuad',
  },

  explosionSmoke: {
    count: [12, 20],
    speed: [0.3, 1.2],
    angle: [0, TAU],
    life: [520, 900],
    size: [2.8, 5],
    sizeEnd: 5,
    drag: 0.93,
    // Rises. Smoke that falls reads as debris, and the blast already has debris.
    gravity: -0.012,
    tint: [0x4a4048, 0x352c36],
    tintEnd: 0x1a1620,
    alphaStart: 0.5,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'linear',
  },

  ember: {
    count: [11, 19],
    speed: [0.6, 2.2],
    angle: [0, TAU],
    life: [500, 950],
    size: [1.2, 2],
    sizeEnd: 0,
    drag: 0.95,
    gravity: 0.045,
    tint: [0xffd60a, 0xff8f1f],
    tintEnd: 0x8a2a10,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot1',
    ease: 'linear',
  },

  // ── Pierce ────────────────────────────────────────────────────────────────────────
  // A pass-through has to read differently from a stop. The shot is still on screen and
  // still moving, so the effect says "it went through": a hard spray thrown out along the
  // line of travel, and a bright hole left behind where it entered.

  pierceSpray: {
    count: [13, 19],
    speed: [2.4, 5.5],
    angle: [0, TAU],
    cone: 0.55,
    life: [110, 220],
    size: [1.5, 2.6],
    sizeEnd: 0,
    drag: 0.87,
    gravity: 0.02,
    tint: [0xffffff, 0xdbe9ff],
    tintEnd: 0x6a7fa5,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  pierceHole: {
    count: [1, 1],
    speed: [0, 0],
    angle: [0, 0],
    life: [160, 160],
    size: [3, 3],
    sizeEnd: 0.3,
    drag: 1,
    gravity: 0,
    tint: [0xffffff],
    tintEnd: 0x4cc9f0,
    alphaStart: 0.95,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_ring8',
    ease: 'easeOutQuad',
  },

  // ── Zones (B-18/B-19) ─────────────────────────────────────────────────────────────
  // "The area effect make small particle come up from it": motes drifting UP off the
  // whole footprint, scattered across the radius by the emitter's `spread`, so the ring
  // reads as ground that is doing something rather than a decal.

  zoneMote: {
    count: [15, 23],
    speed: [0.15, 0.5],
    angle: [-Math.PI / 2 - 0.5, -Math.PI / 2 + 0.5],
    life: [520, 900],
    size: [1.3, 2.3],
    sizeEnd: 0,
    drag: 0.99,
    gravity: -0.02,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 0.75,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot1',
    ease: 'linear',
  },

  zoneStrike: {
    count: [20, 30],
    speed: [0.8, 2.4],
    angle: [-Math.PI / 2 - 0.9, -Math.PI / 2 + 0.9],
    life: [220, 400],
    size: [1.8, 3.2],
    sizeEnd: 0,
    drag: 0.9,
    gravity: -0.03,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutQuad',
  },

  // ── Beams and lasers ──────────────────────────────────────────────────────────────

  beamMote: {
    count: [2, 3],
    speed: [0.2, 0.9],
    angle: [0, TAU],
    life: [140, 260],
    size: [1.3, 2.2],
    sizeEnd: 0,
    drag: 0.9,
    gravity: -0.01,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 0.8,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'linear',
  },

  // ── Entrances and exits ───────────────────────────────────────────────────────────

  emergeDirt: {
    count: [7, 11],
    speed: [0.5, 1.6],
    angle: [-Math.PI / 2 - 0.8, -Math.PI / 2 + 0.8],
    life: [400, 700],
    size: [1.6, 3],
    sizeEnd: 0.2,
    drag: 0.92,
    gravity: 0.06,
    tint: [0x5a4a3a, 0x3d3228],
    tintEnd: 0x241d18,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'linear',
  },

  teleportOut: {
    count: [26, 36],
    speed: [1.5, 4],
    angle: [0, TAU],
    life: [180, 340],
    size: [1.6, 3.2],
    sizeEnd: 0,
    drag: 0.88,
    gravity: 0,
    tint: [0xff006e, 0x4cc9f0, 0xffffff],
    tintEnd: 0x1a0a20,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  teleportIn: {
    count: [24, 34],
    speed: [3, 6],
    angle: [0, TAU],
    life: [160, 280],
    // Converging is faked by starting large and collapsing — the pool has no attractor,
    // and a shrink to nothing at the arrival point reads the same at this size.
    size: [2.6, 3.6],
    sizeEnd: 0,
    drag: 0.8,
    gravity: 0,
    tint: [0x4cc9f0, 0xff006e, 0xffffff],
    tintEnd: 0xffffff,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'easeInQuad',
  },

  // ── Statuses ──────────────────────────────────────────────────────────────────────
  // One preset, tinted per status by the caller. Ten near-identical presets differing
  // only in colour is exactly the kind of data that should not be copy-pasted.

  statusPuff: {
    count: [8, 13],
    speed: [0.3, 1.1],
    angle: [0, TAU],
    life: [280, 480],
    size: [1.3, 2.3],
    sizeEnd: 0,
    drag: 0.9,
    gravity: -0.015,
    tint: 'source',
    tintEnd: 0xffffff,
    alphaStart: 0.85,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_dot2',
    ease: 'easeOutQuad',
  },

  // ── Economy and board ─────────────────────────────────────────────────────────────

  sellPuff: {
    count: [15, 22],
    speed: [0.6, 1.8],
    angle: [0, TAU],
    life: [260, 420],
    size: [1.5, 2.8],
    sizeEnd: 0,
    drag: 0.88,
    gravity: 0.03,
    tint: [0x8d93a3, 0xc8c8d0],
    tintEnd: 0x3f3f46,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'easeOutQuad',
  },

  waveStart: {
    count: [18, 26],
    speed: [1.2, 3],
    angle: [0, TAU],
    life: [340, 560],
    size: [1.8, 3],
    sizeEnd: 0,
    drag: 0.92,
    gravity: 0,
    tint: [0xff6a3d, 0xffd60a],
    tintEnd: 0x882200,
    alphaStart: 0.9,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_spark',
    ease: 'easeOutQuad',
  },

  waveClear: {
    count: [40, 56],
    speed: [1, 3.4],
    angle: [0, TAU],
    life: [420, 750],
    size: [1.8, 3.4],
    sizeEnd: 0,
    drag: 0.93,
    gravity: 0.02,
    tint: [0x6fe08a, 0xffffff, 0xffd60a],
    tintEnd: 0x1f5c33,
    alphaStart: 1,
    alphaEnd: 0,
    blend: 'add',
    texture: 'p_shard4',
    ease: 'easeOutCubic',
  },

  runEnd: {
    count: [60, 85],
    speed: [0.4, 2.2],
    angle: [0, TAU],
    life: [700, 1300],
    size: [1.4, 3],
    sizeEnd: 0,
    drag: 0.96,
    gravity: -0.008,
    tint: [0x8d93a3, 0x4a4048],
    tintEnd: 0x14121a,
    alphaStart: 0.8,
    alphaEnd: 0,
    blend: 'normal',
    texture: 'p_square3',
    ease: 'linear',
  },
} satisfies Record<string, EmitterPreset>;

export type PresetName = keyof typeof PRESETS;
